import { devlog } from '../../util/utils.ts';
import { AsText } from '../text-and-lang/as-text.js';
import { AsStepPanel } from './as-step-panel.js';
import styleText from './step-selector.css?raw';
import type { SetValueOptions } from '../inputs/types';
import { resolveSetValueSilence } from '../inputs/utils';
import { sendEv } from '../../data/events.ts';

type StepUpdateOptions = {
  emitChange?: boolean;
  reflectAttr?: boolean;
  bypassGuards?: boolean;
};

export class AsStepSelector extends HTMLElement {
  static readonly tag = 'as-step-selector' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return ['show-labels', 'current-step', 'hide-panel', 'lock', 'focus-on-change', 'unconstrained', 'auto-hide-labels', 'dynamic-scale'];
  }

  private _prevStep: number = -1;
  private _stepIndex: number = -1;
  private _prevStepId: string = '';
  private _stepId: string = '';
  private _focusOnChange: boolean = false;
  private _unconstrained: boolean = false;
  private _stepPanels: AsStepPanel[] = [];
  private _showLabels: boolean = true;
  private _hidePanel: boolean = false;
  private _observer: MutationObserver;
  private _changeStepHandler?: (e: CustomEvent) => void;
  private _isReflectingCurrentStepAttr = false;
  private _pendingStep: { id: string; emit: boolean } | null = null;
  private _locked: boolean = false;
  private _autoHideLabels: boolean = false;
  private _dynamicScale: boolean = false;
  private _baseNavScale: number = 1;
  private _hoveredNavIndex: number = -1;
  private _navResizeObserver: ResizeObserver | null = null;
  private _lastKnownHostHeight: number = 0;
  private _urlStepInit: boolean = false;
  private _useUrlSync: boolean = false;
  private _initialized: boolean = false;
  private _boundPopState: (() => void) | null = null;

  private get _currentStep(): number {
    return this._stepIndex;
  }

  private set _currentStep(value: number) {
    this._setStepInternal(value, {
      emitChange: false,
      reflectAttr: false,
      bypassGuards: true,
    });
  }

  private get _currentStepId(): string {
    return this._stepId;
  }

  private set _currentStepId(value: string) {
    if (value === this._stepId) {
      return;
    }
    this._prevStepId = this._stepId;
    this._stepId = value;
  }

  private _parseBooleanAttribute(value: string | null): boolean {
    if (value === null) {
      return false;
    }
    if (value === '') {
      return true;
    }
    return value.toLowerCase() === 'true';
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsStepSelector._sheet
    ];
    
    this.shadowRoot.innerHTML = /*html*/`
      <div class="step-nav">
        <div class="step-line-segments"></div>
      </div>
      <div class="step-content">
        <div class="step-panels">
          <slot></slot>
        </div>
      </div>
    `;
    
    this._observer = new MutationObserver(() => this._updateSteps());
  }

  connectedCallback() {
    this._showLabels = this.getAttribute('show-labels') !== 'false';
    this._hidePanel = this._parseBooleanAttribute(this.getAttribute('hide-panel'));
    this._focusOnChange = this.hasAttribute('focus-on-change');
    this._autoHideLabels = this.hasAttribute('auto-hide-labels');
    this._dynamicScale = this.hasAttribute('dynamic-scale');
    
    // Check for ?step in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('step')) {
      this._urlStepInit = true;
      this._useUrlSync = true;
    }

    this._setInitialCurrentStep();
    
    this._observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'hide']
    });
    
    setTimeout(() => {
      this._updateSteps();
      this._updatePanelLayout();
    }, 0);

    // Add window event listener for global step changes
    this._changeStepHandler = (e: CustomEvent) => {
      const { stepId, stepIndex } = e.detail || {};

      let targetIndex = -1;
      if (stepIndex !== undefined) {
        targetIndex = stepIndex;
      } else if (stepId) {
        targetIndex = this._stepPanels.findIndex(panel => panel.id === stepId);
      }

      // Only navigate if it's a different step
      if (targetIndex !== -1 && targetIndex !== this._currentStep) {
        this._goToStep(targetIndex);
      }
    };

    this._boundPopState = () => {
      if (this._useUrlSync && this._initialized) {
        this._syncStepFromUrl();
      }
    };

    window.addEventListener('as-change-step', this._changeStepHandler);
    window.addEventListener('as-force-step', this._handleForceStep as EventListener);
    window.addEventListener('as-app-busy', this._handleAppBusy as EventListener);
    window.addEventListener('as-intrn-configurator-ready', this._handleInit as EventListener);
    window.addEventListener('popstate', this._boundPopState);
    this.addEventListener('change', this._handleChildChange);
    this.addEventListener('as-input-change', this._handleChildChange);

    // Request initial busy state in case we connected after BusyMixin started
    sendEv(window, 'as-get-app-busy', {
      detail: { 
        resolve: (detail: { busy: boolean; keys: string[] }) => {
          this._handleAppBusy({ detail } as any);
        } 
      }
    });
  }

  disconnectedCallback() {
    this._observer.disconnect();
    if (this._changeStepHandler) {
      window.removeEventListener('as-change-step', this._changeStepHandler);
    }
    window.removeEventListener('as-force-step', this._handleForceStep as EventListener);
    window.removeEventListener('as-app-busy', this._handleAppBusy);
    window.removeEventListener('as-intrn-configurator-ready', this._handleInit as EventListener);
    if (this._boundPopState) {
      window.removeEventListener('popstate', this._boundPopState);
    }
    this.removeEventListener('change', this._handleChildChange);
    this.removeEventListener('as-input-change', this._handleChildChange);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (name === 'show-labels') {
      this._showLabels = newValue !== 'false';
      this._updateSteps();
    } else if (name === 'current-step') {
      if (this._isReflectingCurrentStepAttr) {
        return;
      }
      this._setCurrentStepFromAttribute(newValue, { emitChange: true });
    } else if (name === 'hide-panel') {
      this._hidePanel = this._parseBooleanAttribute(newValue);
      this._updatePanelVisibility();
      this._updatePanelLayout();
    } else if (name === 'lock') {
      this._setLocked(newValue !== null);
    } else if (name === 'focus-on-change') {
      this._focusOnChange = newValue !== null;
    } else if (name === 'auto-hide-labels') {
      this._autoHideLabels = newValue !== null;
      this._updateSteps();
    } else if (name === 'dynamic-scale') {
      this._dynamicScale = newValue !== null;
      this._updateBaseScale();
      this._updateSteps();
    }
  }

  private _setInitialCurrentStep() {
    const currentStepAttr = this.getAttribute('current-step') ?? '0';
    this._setCurrentStepFromAttribute(currentStepAttr, { emitChange: false });
  }

  private _setCurrentStepFromAttribute(value: string | null, options: StepUpdateOptions = {}) {
    if (value === null) {
      return;
    }

    const stepOptions: StepUpdateOptions = {
      emitChange: options.emitChange ?? true,
      reflectAttr: false,
      bypassGuards: true,
    };

    // Check if it's a number or string ID
    const numericValue = parseInt(value, 10);
    const isNumeric = !isNaN(numericValue) && numericValue.toString() === value.trim();

    if (isNumeric) {
      // Numeric index path - simple and direct
      this._setStepInternal(numericValue, stepOptions);
    } else {
      // String ID path - need to find the panel by ID
      const targetIndex = this._stepPanels.findIndex(panel => panel.id === value);

      if (targetIndex !== -1) {
        this._setStepInternal(targetIndex, stepOptions);
      } else {
        // Step not found yet, store as pending for updateSteps() to handle
        this._pendingStep = { id: value, emit: stepOptions.emitChange ?? true };
      }
    }
  }

  private _updateSteps() {
    // Get all step panels, including hidden ones for internal tracking
    this._stepPanels = Array.from(this.querySelectorAll('as-step-panel'));
    this._renderStepNav();

    if (this._pendingStep) {
      const { id, emit } = this._pendingStep;
      const targetIndex = this._stepPanels.findIndex(panel => panel.id === id);
      if (targetIndex !== -1) {
        this._setStepInternal(targetIndex, {
          emitChange: emit,
          reflectAttr: false,
          bypassGuards: true,
        });
        this._pendingStep = null;
      }
    }

    // Handle initial URL sync if requested
    if (this._urlStepInit && this._initialized && this._stepPanels.length > 0) {
      const synced = this._syncStepFromUrl();
      if (synced) {
        this._urlStepInit = false; // Only clear if we actually attempted a sync with panels
      }
    }

    this._updateStepStates();
    this._updatePanelVisibility();
    this._updatePanelLayout();
  }

  private _handleInit = () => {
    this._initialized = true;
    if (this._urlStepInit) {
      this._updateSteps();
    }
  };

  private _renderStepNav() {
    const stepNav = this.shadowRoot?.querySelector<HTMLElement>('.step-nav');
    const lineSegments = this.shadowRoot?.querySelector('.step-line-segments') as HTMLElement;
    if (!stepNav || !lineSegments) return;

    // Clear existing items (except line segments container)
    Array.from(stepNav.children).forEach(child => {
      if (!child.classList.contains('step-line-segments')) {
        child.remove();
      }
    });
    lineSegments.innerHTML = '';

    // Filter out hidden step panels
    const visibleStepPanels = this._stepPanels.filter(panel => !panel.hasAttribute('hide'));

    // If no visible step panels, create default step 0
    if (visibleStepPanels.length === 0) {
      const stepItem = document.createElement('div');
      stepItem.className = 'step-item';

      const stepButton = document.createElement('div');
      stepButton.className = 'step-button';
      stepButton.textContent = '0';
      stepItem.appendChild(stepButton);

      stepNav.appendChild(stepItem);
      return;
    }

    // Create line segments (visible steps - 1)
    for (let i = 0; i < visibleStepPanels.length - 1; i++) {
      const segment = document.createElement('div');
      segment.className = 'step-line-segment';
      lineSegments.appendChild(segment);
    }

    visibleStepPanels.forEach((panel, visibleIndex) => {
      const actualIndex = this._stepPanels.indexOf(panel);
      const stepItem = document.createElement('div');
      stepItem.className = 'step-item';

      const stepButton = document.createElement('div');
      stepButton.className = 'step-button';
      stepButton.textContent = (visibleIndex + 1).toString();

      // Check if this step should be disabled (either directly disabled or blocked by earlier disabled step)
      const isBlocked = this._isStepBlocked(actualIndex);

      if (isBlocked) {
        stepButton.classList.add('disabled');
      } else {
        // Prevent duplicate event listeners
        stepButton.onclick = () => this._goToStep(actualIndex);
      }

      stepItem.appendChild(stepButton);

      if (this._showLabels) {
        const label = document.createElement('as-text') as any;
        label.className = 'step-label';
        const dictKey = panel.getAttribute('label-dict-key');
        const defaultText = panel.getAttribute('label-default-text');

        if (dictKey) {
          label.setAttribute('dict-key', dictKey);
        }
        if (defaultText) {
          label.textContent = defaultText;
        }

        // Add disabled class and prevent interactions if blocked
        if (isBlocked) {
          label.classList.add('disabled');
        } else {
          // Prevent duplicate event listeners by using onclick properties
          label.onclick = () => this._goToStep(actualIndex);
        }

        stepItem.appendChild(label);
      }

      stepItem.onmouseenter = () => {
        if (!this._dynamicScale) return;
        this._hoveredNavIndex = visibleIndex;
        this._applyScaleToNavItems();
      };

      stepNav.appendChild(stepItem);
    });

    stepNav.onmouseleave = () => {
      if (!this._dynamicScale) return;
      this._hoveredNavIndex = -1;
      this._applyScaleToNavItems();
    };

    if (this._dynamicScale) {
      this._updateBaseScale();
      if (!this._navResizeObserver) {
        this._navResizeObserver = new ResizeObserver(() => {
          if (this.offsetHeight !== this._lastKnownHostHeight) {
            this._updateBaseScale();
          }
        });
        this._navResizeObserver.observe(this);
      }
    }
  }

  private _updateStepStates() {
    const buttons = this.shadowRoot?.querySelectorAll('.step-button');
    const segments = this.shadowRoot?.querySelectorAll('.step-line-segment');
    const labels = this.shadowRoot?.querySelectorAll('.step-label');
    if (!buttons) return;

    // Get visible step panels (non-hidden) to match with buttons/labels
    const visibleStepPanels = this._stepPanels.filter(panel => !panel.hasAttribute('hide'));

    buttons.forEach((button, visibleIndex) => {
      const panel = visibleStepPanels[visibleIndex];
      const actualIndex = this._stepPanels.indexOf(panel);

      button.className = 'step-button';

      if (this._isStepBlocked(actualIndex)) {
        button.classList.add('disabled');
      } else if (actualIndex < this._currentStep) {
        button.classList.add('completed');
      } else if (actualIndex === this._currentStep) {
        button.classList.add('current');
      }
    });

    // Update labels - labels for completed steps should be green, disabled/blocked steps should be grayed
    labels.forEach((label, visibleIndex) => {
      const panel = visibleStepPanels[visibleIndex];
      const actualIndex = this._stepPanels.indexOf(panel);

      label.className = 'step-label';

      if (this._isStepBlocked(actualIndex)) {
        label.classList.add('disabled');
      } else if (actualIndex < this._currentStep) {
        label.classList.add('completed');
      }
    });

    // Update line segments - segments leading up to current step should be green
    segments.forEach((segment, visibleIndex) => {
      const panel = visibleStepPanels[visibleIndex];
      const actualIndex = this._stepPanels.indexOf(panel);

      segment.className = 'step-line-segment';
      if (actualIndex < this._currentStep) {
        segment.classList.add('completed');
      }
    });
  }

  private _updatePanelVisibility() {
    let hasCurrent = false;
    this._stepPanels.forEach((panel, index) => {
      const isCurrent = index === this._currentStep;
      if (!this._hidePanel && isCurrent) {
        panel.setAttribute('show', '');
      } else {
        panel.removeAttribute('show');
      }

      if (isCurrent) {
        hasCurrent = true;
        const panelId = panel.id || '';
        if (this._currentStepId !== panelId) {
          this._currentStepId = panelId;
        }
      }
    });

    if (!hasCurrent && this._currentStepId !== '') {
      this._currentStepId = '';
    }
  }

  private _updatePanelLayout() {
    const stepPanels = this.shadowRoot?.querySelector('.step-panels');
    if (!stepPanels) return;

    stepPanels.classList.toggle('hidden', this._hidePanel);
  }

  private _isStepBlocked(stepIndex: number): boolean {
    let isBlocked = false
    if(this._stepPanels?.[stepIndex]===undefined)return true
    // A step is blocked if it's directly disabled
    if (this._stepPanels[stepIndex].hasAttribute('disabled')) {
      isBlocked = true;
    }

    // A step is blocked if going forward from current position and there's a disabled step in the way
    else if (stepIndex > this._currentStep) {
      for (let i = this._currentStep + 1; i <= stepIndex; i++) {
        if (this._stepPanels[i].hasAttribute('disabled')) {
          isBlocked =  true;
        }
      }
    }

    else isBlocked =  false;
    return isBlocked
  }

  private _clampStepIndex(stepIndex: number): number {
    if (this._stepPanels.length === 0) {
      return Math.max(stepIndex, 0);
    }
    if (stepIndex < 0) {
      return 0;
    }
    if (stepIndex >= this._stepPanels.length) {
      return this._stepPanels.length - 1;
    }
    return stepIndex;
  }

  private _reflectCurrentStepAttr(stepIndex: number): void {
    if (this._isReflectingCurrentStepAttr) {
      return;
    }
    this._isReflectingCurrentStepAttr = true;
    try {
      this.setAttribute('current-step', stepIndex.toString());
    } finally {
      this._isReflectingCurrentStepAttr = false;
    }
  }

  private _updateBaseScale() {
    const nav = this.shadowRoot?.querySelector('.step-nav') as HTMLElement | null;
    if (!nav) return;

    if (!this._dynamicScale) {
      this._baseNavScale = 1;
      nav.style.transform = '';
      nav.style.transformOrigin = '';
      this._applyScaleToNavItems();
      return;
    }

    const hostHeight = this.offsetHeight;
    if (hostHeight === 0) return;
    this._lastKnownHostHeight = hostHeight;

    const visibleCount = this._stepPanels.filter(p => !p.hasAttribute('hide')).length;
    if (visibleCount === 0) return;

    const items = this.shadowRoot?.querySelectorAll('.step-item') as NodeListOf<HTMLElement>;
    if (!items?.length) return;
    const naturalItemHeight = (items[0] as HTMLElement).offsetHeight;
    const naturalNavHeight = visibleCount * naturalItemHeight;
    if (naturalNavHeight === 0) return;

    this._baseNavScale = Math.min(1, hostHeight / naturalNavHeight);

    nav.style.transformOrigin = 'left center';
    nav.style.transform = this._baseNavScale < 1 ? `scale(${this._baseNavScale})` : '';

    this._applyScaleToNavItems();
  }

  private _applyScaleToNavItems() {
    const items = this.shadowRoot?.querySelectorAll('.step-item');
    if (!items) return;

    // Only show fish-eye when items are meaningfully compressed
    const fisheyeActive = this._baseNavScale < 0.8;

    items.forEach((item: any, index) => {
      let blend = 0;
      if (fisheyeActive && this._hoveredNavIndex !== -1) {
        const dist = Math.abs(index - this._hoveredNavIndex);
        if (dist === 0) blend = 1;
        else if (dist === 1) blend = 0.5;
        else if (dist === 2) blend = 0.2;
      }

      const targetEffective = this._baseNavScale + blend * (1 - this._baseNavScale);
      const itemScale = targetEffective / this._baseNavScale;
      item.style.transform = `scale(${itemScale})`;
      item.style.zIndex = blend > 0 ? String(2 + Math.round(blend * 8)) : '';
    });
  }

  private _handleChildChange = (e: Event) => {
    if (!this._focusOnChange || this._locked) return;

    // Support opting out of auto-focus/navigation via event detail
    if ((e as CustomEvent).detail?.dontFocus === true) return;

    const target = e.target as HTMLElement;
    const panel = target.closest('as-step-panel') as AsStepPanel;

    if (panel && this._stepPanels.includes(panel)) {
      const targetIndex = this._stepPanels.indexOf(panel);

      if (targetIndex !== -1) {
        // Ensure the panel is visible (auto-focus should never hide/toggle)
        if (this._hidePanel) {
          this._hidePanel = false;
          this._updatePanelVisibility();
          this._updatePanelLayout();
        }

        // Navigate to the step if it's not already current
        if (targetIndex !== this._currentStep) {
          this.setStep(targetIndex, { silenceChangeEv: true });
        }
      }
    }
  };

  private _emitStepChange(prevIndex: number, prevId: string, newIndex: number, newId: string): void {
    const detail = {
      stepId: newId,
      index: newIndex,
      prevStepId: prevId,
      prevStepIdx: prevIndex,
      prevStepIndex: prevIndex,
      newStepId: newId,
      newStepIdx: newIndex,
      currentStepId: newId,
      currentStepIndex: newIndex,
      phase: 'change' as const,
    };

    sendEv(this, 'as-step-changed', { detail });
    sendEv(this, 'as-step-change', { detail });
  }

  private _handleAppBusy = (ev: CustomEvent<{ busy: boolean; keys: string[] }>) => {
    if (ev.detail?.busy === true) {
      this.setAttribute('lock', '');
    } else {
      this.removeAttribute('lock');
    }
  };

  private _handleForceStep = (e: CustomEvent) => {
    const { stepId, stepIndex, silenceChange } = e.detail || {};
    let targetIndex = -1;
    if (stepIndex !== undefined) {
      targetIndex = stepIndex;
    } else if (stepId) {
      targetIndex = this._stepPanels.findIndex(panel => panel.id === stepId);
    }
    if (targetIndex !== -1) {
      this.forceStep(targetIndex, { silenceChangeEv: silenceChange });
    }
  };

  private _setLocked(locked: boolean): void {
    if (this._locked === locked) return;
    this._locked = locked;

    const stepPanels = this.shadowRoot?.querySelector('.step-panels');
    if (stepPanels) {
      if (locked) {
        stepPanels.setAttribute('inert', '');
      } else {
        stepPanels.removeAttribute('inert');
      }
    }

  }

  private _setStepInternal(stepIndex: number, options: StepUpdateOptions = {}): boolean {
    const { emitChange = true, reflectAttr = true, bypassGuards = false } = options;

    if (!bypassGuards && this._isStepBlocked(stepIndex)) {
      return false;
    }

    const clampedIndex = this._clampStepIndex(stepIndex);
    const previousIndex = this._stepIndex;
    const previousId = this._stepId;

    if (previousIndex === clampedIndex) {
      if (reflectAttr) {
        this._reflectCurrentStepAttr(clampedIndex);
      }
      return false;
    }

    this._prevStep = previousIndex;
    this._stepIndex = clampedIndex;

    const newPanel = this._stepPanels[clampedIndex];
    const newId = newPanel?.id ?? '';
    this._currentStepId = newId;

    if (reflectAttr) {
      this._reflectCurrentStepAttr(clampedIndex);
    }

    this._updateStepStates();
    this._updatePanelVisibility();

    // Only update URL if URL sync is enabled and initial sync is OVER
    if (this._useUrlSync && !this._urlStepInit && this._initialized) {
      this._updateUrlStep(clampedIndex);
    }

    if (emitChange) {
      this._emitStepChange(previousIndex, previousId, clampedIndex, newId);
    }

    return true;
  }

  private _syncStepFromUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    if (stepParam === null) return true; // Nothing to sync, consider it "done"

    let targetIndex = -1;
    const stepNum = parseInt(stepParam, 10);
    const isNumeric = !isNaN(stepNum) && stepNum.toString() === stepParam.trim();

    if (isNumeric) {
      // Convert from user-facing 1-based index to internal 0-based index
      targetIndex = stepNum - 1;
    } else {
      // Treat as string ID
      targetIndex = this._stepPanels.findIndex(panel => panel.id === stepParam);
    }
    
    // Only navigate if it's within bounds
    if (targetIndex >= 0 && targetIndex < this._stepPanels.length) {
      if (!this.hasAttribute('unconstrained') && this._isStepBlocked(targetIndex)) {
        devlog(`[STEP-SELECTOR] URL sync failed: Step ${stepParam} is currently blocked/disabled.`);
        return true; // We attempted it, it's just blocked
      }
      
      this._setStepInternal(targetIndex, {
        emitChange: true,
        reflectAttr: true,
        bypassGuards: true // URL params should generally be authoritative
      });
      return true;
    } else {
      devlog(`[STEP-SELECTOR] URL sync deferred: Step ${stepParam} not found in current panels.`);
      return false; // Panels might still be loading
    }
  }

  private _updateUrlStep(index: number): void {
    const url = new URL(window.location.href);
    const userFacingStep = (index + 1).toString();
    
    if (url.searchParams.get('step') === userFacingStep) return;

    url.searchParams.set('step', userFacingStep);
    window.history.replaceState({}, '', url.toString());
  }

  public setStep(stepIndex: number, opt?: SetValueOptions): boolean {
    if (this._locked) return false;
    const { silenceChange } = resolveSetValueSilence(opt);

    return this._setStepInternal(stepIndex, {
      emitChange: !silenceChange,
      reflectAttr: true,
      bypassGuards: false,
    });
  }

  /**
   * Force navigation to a step, bypassing locks and blocked-step guards.
   * Useful during project initialization or hard resets.
   */
  public forceStep(stepIndex: number, opt?: SetValueOptions): boolean {
    const { silenceChange } = resolveSetValueSilence(opt);
    // forceStep is used for hard resets (new project/session) and must guarantee the
    // panel is visible — otherwise a panel folded via the click-to-close toggle
    // (goToStep) survives the reset when the target step equals the current step,
    // since setStepInternal no-ops on a same-index call and never touches hidePanel.
    if (this._hidePanel) {
      this._hidePanel = false;
      this._updatePanelVisibility();
      this._updatePanelLayout();
    }
    return this._setStepInternal(stepIndex, {
      emitChange: !silenceChange,
      reflectAttr: true,
      bypassGuards: true,
    });
  }

  private _goToStep(stepIndex: number) {
    if (this._locked) return;
    if (stepIndex < 0 || stepIndex >= this._stepPanels.length) {
      return;
    }

    if (stepIndex === this._currentStep) {
      this._hidePanel = !this._hidePanel;
      this._updatePanelVisibility();
      return;
    } else if (this._hidePanel) {
      this._hidePanel = false;
      this._updatePanelVisibility();
    }

    const changed = this.setStep(stepIndex);
    if (!changed) {
      devlog(`[STEP-SELECTOR] Unable to navigate to step ${stepIndex}`);
    }
  }

  public nextStep() {
    if (this._locked) return;
    if (this._currentStep < this._stepPanels.length - 1) {
      this._goToStep(this._currentStep + 1);
    }
  }

  public previousStep() {
    if (this._locked) return;
    if (this._currentStep > 0) {
      this._goToStep(this._currentStep - 1);
    }
  }

  public getCurrentStep(): number {
    return this._currentStep;
  }

  public getTotalSteps(): number {
    return this._stepPanels.length;
  }

  get value(): number {
    return this._stepIndex;
  }

  set value(stepIndex: number) {
    this.setStep(stepIndex);
  }
}

if (!customElements.get(AsStepSelector.tag)) {
  customElements.define(AsStepSelector.tag, AsStepSelector);
} else {
  console.warn("as-step-selector already defined");
} 
